import { todayDateRangeInUserTimeZone } from '@/helpers';
import { cloneDeep } from 'lodash-es';
import { MappingProblemItem } from '@/models/mappingProblem';
import { createMappingProblemPageHeaders } from '@/pages/admin/mappingProblem/MappingProblemPageHeaders';
import { isRequestCanceled } from '@/services/baseService';
import { mappingProblemService } from '@/services/mappingProblemService';
import { createLoadingGenerationTracker } from '@/stores/composables/createLoadingGenerationTracker';
import { createStorePersistConfig } from '@/stores/composables/createStorePersistConfig';
import { useFilterPanelState } from '@/stores/composables/useFilterPanelState';
import { IPageOptions } from '@/types';
import type { IMappingProblemFilters } from '@/types/mappingProblem';
import type { DataTableHeader } from '@/types/vuetify';

const defaultSortBy = [{ key: 'id', order: 'desc' as const }];

function createMappingProblemStoreSetup(storeId: string) {
    // --- state ---
    const items = shallowRef<MappingProblemItem[]>([]);
    const total = ref<number>(0);
    const loading = ref(false);
    const loadGeneration = createLoadingGenerationTracker();

    // --- filters ---
    const filters = reactive<IMappingProblemFilters>({
        mappingProblemDate: {
            value: todayDateRangeInUserTimeZone(),
            title: 'mappingProblem.createdDateFilter',
            allowSaveState: true,
            disableClear: true,
        },
        mappingProblemSearch: {
            value: null,
            title: 'mappingProblem.search',
            allowSaveState: true,
        },
        mappingProblemTypeId: {
            value: null,
            title: 'mappingProblem.typeFilter',
            allowSaveState: true,
        },
        mappingProblemStatusId: {
            value: null,
            title: 'mappingProblem.statusFilter',
            allowSaveState: true,
        },
        expandAll: {
            value: false,
            title: 'mappingProblem.expandAll',
            allowSaveState: false,
            disableClear: true,
        },
    });

    // --- filter panel ---
    const { hiddenFilters, visibleFiltersMobile, filterOrder, availableFilters, useButtonForFiltersApply, saveFiltersState, visibleFilters } =
        useFilterPanelState(filters);

    // --- options ---
    const options = ref<IPageOptions>({
        itemsPerPage: 100,
        page: 1,
        rowsPerPageItems: [100, 200, 500],
        sortBy: [...defaultSortBy],
    });

    // --- reset ---
    function resetFilters() {
        filters.mappingProblemDate.value = todayDateRangeInUserTimeZone();
        filters.mappingProblemSearch.value = null;
        filters.mappingProblemTypeId.value = null;
        filters.mappingProblemStatusId.value = null;
        filters.expandAll.value = false;
    }

    // --- table ---
    const headers = computed<DataTableHeader[]>(() => createMappingProblemPageHeaders());

    const availableColumns = computed(() => headers.value.filter((header) => header.title));
    const hiddenColumns = ref<string[]>([]);
    const tableHeaders = computed<DataTableHeader[]>(() =>
        headers.value.filter((header) => header.key && (!hiddenColumns.value.includes(header.key) || !header.title) && !header.hidden)
    );

    // --- persistence ---
    const reloadFiltersFromStorage = () => {
        resetFilters();
    };

    // --- actions ---
    async function loadData() {
        const loadId = loadGeneration.next();
        loading.value = true;
        try {
            const filtersToSend = cloneDeep(filters);
            // expandAll is a UI-only toggle; do not send it to the backend
            const { expandAll: _expandAll, ...filtersForRequest } = filtersToSend;

            const params = {
                filters: filtersForRequest as IMappingProblemFilters,
                options: {
                    sortBy: options.value.sortBy,
                    rowsPerPageItems: options.value.rowsPerPageItems,
                    page: options.value.page,
                    itemsPerPage: options.value.itemsPerPage,
                },
                requiredData: [],
                requiredDictionaries: [],
            };

            const response = await mappingProblemService.list(params);

            items.value = response.data.rows.map((row) => new MappingProblemItem(row));
            total.value = response.data.total;
        } catch (e) {
            if (isRequestCanceled(e)) return;
            throw e;
        } finally {
            if (loadGeneration.isLatest(loadId)) {
                loading.value = false;
            }
        }
    }

    function updateOptions(value: Partial<IPageOptions>) {
        if (value.page !== undefined && value.page !== options.value.page) {
            options.value.page = value.page;
            loadData();
        }
        if (value.itemsPerPage !== undefined && value.itemsPerPage !== options.value.itemsPerPage) {
            options.value.itemsPerPage = value.itemsPerPage;
            options.value.page = 1;
            loadData();
        }
    }

    function clearData() {
        items.value = [];
        total.value = 0;
        options.value.page = 1;
        loading.value = false;
    }

    // --- return ---
    return {
        storeId,
        items,
        total,
        filters,
        hiddenFilters,
        visibleFiltersMobile,
        filterOrder,
        availableFilters,
        useButtonForFiltersApply,
        saveFiltersState,
        visibleFilters,
        resetFilters,
        reloadFiltersFromStorage,
        headers,
        tableHeaders,
        availableColumns,
        hiddenColumns,
        loading,
        options,
        loadData,
        updateOptions,
        clearData,
        stats: undefined,
    };
}

export function useMappingProblemStore(storeId: string = 'mappingProblem') {
    const useStore = defineStore(storeId, () => createMappingProblemStoreSetup(storeId), {
        persist: createStorePersistConfig(storeId, 'mappingProblem', defaultSortBy),
    });
    return useStore();
}
